import React from 'react'
import type { KambiSportName } from '@mobi/api-types/kambi/sports'
import { Icon, IconName } from '@mobi/component-library/Assets/Icon/Icon'

type SportsIconProps = {
  sport?: KambiSportName | null | undefined
  size?: React.ComponentProps<typeof Icon>['size']
  color?: React.ComponentProps<typeof Icon>['color']
}

export const SportsIcon: React.FC<SportsIconProps> = ({ sport, size = '1.4rem', color }) => {
  const iconName = (sport && kambiSportToIconMap[sport]) || 'SportsDefault'
  return <Icon size={size} name={iconName} color={color} />
}

// =============
// Local Helpers
// =============

const kambiSportToIconMap: Partial<Record<KambiSportName, IconName>> = {
  AMERICAN_FOOTBALL: 'SportsAmericanFootball',
  ASIAN_GAMES: 'SportsAsianGames',
  ATHLETICS: 'SportsAthletics',
  AUSTRALIAN_RULES: 'SportsAustralianRules',
  BADMINTON: 'SportsBadminton',
  BANDY: 'SportsBandy',
  BASEBALL: 'SportsBaseball',
  BASKETBALL: 'SportsBasketball',
  BEACH_SOCCER: 'SportsBeachSoccer',
  BEACH_VOLLEYBALL: 'SportsBeachVolley',
  BOWLING: 'SportsBowling',
  BOWLS: 'SportsBowls',
  BOXING: 'SportsBoxing',
  BRANNBOLL: 'SportsBrannboll',
  BRIDGE: 'SportsBridge',
  CHESS: 'SportsChess',
  COMMONWEALTH_GAMES: 'SportsCommonwealthGames',
  CRICKET: 'SportsCricket',
  CURLING: 'SportsCurling',
  CYCLING: 'SportsCycling',
  CYCLO_CROSS: 'SportsCycloCross',
  DARTS: 'SportsDarts',
  DISC_GOLF: 'SportsDiscGolf',
  DIVING: 'SportsDiving',
  DRONE_RACING: 'SportsDroneRacing',
  EQUESTRIAN: 'SportsEquestrian',
  ESPORTS: 'SportsEsports',
  E_BASKETBALL: 'SportsEsportsBasketball',
  FIELD_HOCKEY: 'SportsFieldHockey',
  FIGURE_SKATING: 'SportsFigureSkating',
  FLOORBALL: 'SportsFloorball',
  FOOTBALL: 'SportsFootball',
  FORMULA_1: 'SportsFormula1',
  FORTUNE_TELLER: 'SportsFortuneTeller',
  FUTSAL: 'SportsFutsal',
  GAA_FOOTBALL: 'SportsGaelicSports',
  GAA_HURLING: 'SportsGaelicSports',
  GALLOPS: 'SportsGallops',
  GOLF: 'SportsGolf',
  GREYHOUNDS: 'SportsGreyhounds',
  HANDBALL: 'SportsHandball',
  HAPPY_HOUR: 'SportsHappyHour',
  HORSE_RACING: 'SportsHorseRacing',
  ICE_HOCKEY: 'SportsIceHockey',
  INDOOR_FOOTBALL: 'SportsIndoorFootball',
  INDOOR_HOCKEY: 'SportsIndoorHockey',
  INLINE_HOCKEY: 'SportsInlineHockey',
  INTERNATIONAL_RULES: 'SportsInternationalRules',
  LACROSSE: 'SportsLacrosse',
  MEDITERRANEAN_GAMES: 'SportsMediterraneanGames',
  MOTORSPORTS: 'SportsMotorsports',
  MOUNTAIN_BIKE: 'SportsMountainBike',
  NETBALL: 'SportsNetball',
  OLYMPIC_GAMES: 'SportsOlympicGames',
  ORIENTEERING: 'SportsOrienteering',
  PADEL_TENNIS: 'SportsPadel',
  PESAPALLO: 'SportsPesapallo',
  PICKLEBALL: 'SportsPickleball',
  POKER: 'SportsPoker',
  POLITICS: 'SportsPolitics',
  POOL: 'SportsPool',
  RACKETLON: 'SportsRacketlon',
  RINK_HOCKEY: 'SportsRinkHockey',
  ROLLER_HOCKEY: 'SportsRollerHockey',
  ROWING: 'SportsRowing',
  RUGBY_LEAGUE: 'SportsRugbyLeague',
  RUGBY_UNION: 'SportsRugbyUnion',
  SNOOKER: 'SportsSnooker',
  SOFTBALL: 'SportsSoftball',
  SQUASH: 'SportsSquash',
  SUMO: 'SportsSumo',
  SURFING: 'SportsSurfing',
  SWIMMING: 'SportsSwimming',
  TABLE_TENNIS: 'SportsTableTennis',
  TABLEHOCKEY: 'SportsTablehockey',
  TABTOUCH_EXCLUSIVES_RWWA: 'SportsTabtouchExclusivesRwwa',
  TENNIS: 'SportsTennis',
  TROTTING: 'SportsTrotting',
  TV_NOVELTY: 'SportsTvNovelty',
  MARTIAL_ARTS: 'SportsUfcMma', // TODO: confirm this
  UNIVERSIADE: 'SportsUniversiade',
  VOLLEYBALL: 'SportsVolleyball',
  WATER_POLO: 'SportsWaterPolo',
  WEIGHTLIFTING: 'SportsWeightlifting',
  WINTER_OLYMPIC_GAMES: 'SportsWinterOlympicGames',
  WINTERSPORTS: 'SportsWinterSports',
  'WINTER-XGAMES': 'SportsWinterXGames',
  WRESTLING: 'SportsWrestling',
  YACHTING: 'SportsYachting',
}

// cspell:words BRANNBOLL FLOORBALL FUTSAL MOTORSPORTS PADEL PESAPALLO PICKLEBALL RACKETLON
// cspell:words TABLEHOCKEY UNIVERSIADE WINTERSPORTS XGAMES
